import { type Table } from '@tanstack/react-table';
import { useMemo } from 'react';

interface UseTablePaginationProps<T> {
  table: Table<T>;
  maxVisiblePages?: number;
}

interface UseTablePaginationReturn {
  currentPage: number;
  canNextPage: boolean;
  canPreviousPage: boolean;
  pageNumbers: (number | 'ellipsis')[];
  pageSize: number;
  totalPages: number;
  nextPage: () => void;
  previousPage: () => void;
  setPageIndex: (index: number) => void;
}

export const useTablePagination = <T>({
  table,
  maxVisiblePages = 7,
}: UseTablePaginationProps<T>): UseTablePaginationReturn => {
  const {
    getState,
    getPageCount,
    getCanPreviousPage,
    getCanNextPage,
    nextPage,
    previousPage,
    setPageIndex,
  } = table;
  const { pagination } = getState();
  const pageCount = getPageCount();

  const currentPage = pagination.pageIndex + 1;
  const totalPages = pageCount;
  const canPreviousPage = getCanPreviousPage();
  const canNextPage = getCanNextPage();

  const pageNumbers = useMemo((): (number | 'ellipsis')[] => {
    if (totalPages <= 0) return [];

    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | 'ellipsis')[] = [1];

    const startPage = Math.max(2, currentPage - 1);
    const endPage = Math.min(totalPages - 1, currentPage + 1);

    if (startPage > 2) {
      pages.push('ellipsis');
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages - 1) {
      pages.push('ellipsis');
    }

    pages.push(totalPages);

    return pages;
  }, [currentPage, totalPages, maxVisiblePages]);

  return {
    currentPage,
    canNextPage,
    canPreviousPage,
    pageNumbers,
    pageSize: pagination.pageSize,
    totalPages,
    nextPage,
    previousPage,
    setPageIndex: (index: number): void => setPageIndex(index),
  };
};
